import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { vi } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Registry of supported organization timezones.
 * To expand: add entries here and mirror backend Organization.settings.timezone enum.
 */
export const TIMEZONE_CONFIG = {
  'Asia/Ho_Chi_Minh': {
    label: 'Asia/Ho_Chi_Minh (ICT)',
    locale: 'vi-VN',
  },
} as const;

export type OrgTimezone = keyof typeof TIMEZONE_CONFIG;

export const SUPPORTED_TIMEZONES = Object.keys(TIMEZONE_CONFIG) as OrgTimezone[];

export const DEFAULT_TIMEZONE: OrgTimezone = 'Asia/Ho_Chi_Minh';

const DATE_FNS_LOCALES: Record<(typeof TIMEZONE_CONFIG)[OrgTimezone]['locale'], Locale> = {
  'vi-VN': vi,
};

const getDateFnsLocale = (timeZone: OrgTimezone): Locale =>
  DATE_FNS_LOCALES[TIMEZONE_CONFIG[timeZone].locale];

export const resolveTimezone = (value?: string | null): OrgTimezone => {
  if (value && value in TIMEZONE_CONFIG) {
    return value as OrgTimezone;
  }
  return DEFAULT_TIMEZONE;
};

export const getTimezoneLabel = (timeZone: OrgTimezone): string =>
  TIMEZONE_CONFIG[timeZone].label;

/**
 * Wall-clock date/time in org timezone → UTC instant for API/DB storage.
 */
export const zonedDateTimeToUtc = (
  datePart: string,
  timePart: string,
  timeZone: OrgTimezone
): Date => {
  const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
  return fromZonedTime(`${datePart}T${normalizedTime}`, timeZone);
};

/**
 * UTC instant → formatted string in org timezone (weekday/month names follow org locale).
 */
export const formatInOrgTimezone = (
  value: Date | string | number | null | undefined,
  formatStr: string,
  timeZone: OrgTimezone
): string => {
  if (value == null) return '';
  return formatInTimeZone(value, timeZone, formatStr, {
    locale: getDateFnsLocale(timeZone),
  });
};

/** yyyy-MM-dd in org timezone → local Date for date-only pickers. */
export const parseOrgDateOnly = (datePart: string): Date => {
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** UTC instant → date/time parts in org timezone (for forms). */
export const extractOrgDateParts = (
  value: Date | string | number | null | undefined,
  timeZone: OrgTimezone
): { date: string; time: string } | null => {
  if (value == null) return null;
  return {
    date: formatInTimeZone(value, timeZone, 'yyyy-MM-dd'),
    time: formatInTimeZone(value, timeZone, 'HH:mm'),
  };
};

/**
 * UTC instant → Date with wall-clock components in org timezone for date pickers / local Date UIs.
 */
export const toCalendarDisplayDate = (
  value: Date | string | number | null | undefined,
  timeZone: OrgTimezone
): Date => {
  const parts = extractOrgDateParts(value, timeZone);
  if (!parts) return new Date(value as string | number | Date);
  const [year, month, day] = parts.date.split('-').map(Number);
  const [hours, minutes] = parts.time.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};
